import * as React from 'react'
import { Percent } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { DiscountCodeEventRestrictions } from '@/pages/DiscountCodeEventRestrictions'
import type { DiscountCode, EventSummary } from '@/pages/DiscountCodesPage'

interface MembershipLevelSummary {
  id: number
  name: string
}

interface MembershipLevelsResponse {
  membership_levels: MembershipLevelSummary[]
}

interface EventsResponse {
  events: EventSummary[]
}

const EMPTY_FORM = {
  code: '',
  reductionType: 'percent',
  amount: '',
  usageCap: '',
  startsOn: '',
  endsOn: '',
  appliesTo: ['application'] as string[],
  membershipLevelIds: [] as number[],
  eventIds: [] as number[],
}

type DiscountCodeFormState = typeof EMPTY_FORM
type SetForm = React.Dispatch<React.SetStateAction<DiscountCodeFormState>>

export function DiscountCodeForm({
  discountCode,
  onSaved,
}: {
  discountCode?: DiscountCode
  onSaved?: (code: DiscountCode) => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = React.useState(() => formForDiscountCode(discountCode))
  const levelsQuery = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const eventsQuery = useQuery({
    queryKey: ['events', 'discount-code-restrictions'],
    queryFn: () => getJSON<EventsResponse>('/api/v1/events'),
    retry: false,
  })
  const saveCode = useMutation({
    mutationFn: () =>
      discountCode
        ? patchJSON<{ discount_code: DiscountCode }>(
            `/api/v1/discount_codes/${discountCode.id}`,
            discountCodePayload(form, true),
          )
        : postJSON<{ discount_code: DiscountCode }>(
            '/api/v1/discount_codes',
            discountCodePayload(form, false),
          ),
    onSuccess: async (response) => {
      if (!discountCode) setForm(EMPTY_FORM)
      await queryClient.invalidateQueries({ queryKey: ['discount-codes'] })
      onSaved?.(response.discount_code)
    },
  })

  return (
    <section className="p-4">
      <h2 className="flex items-center gap-2 pb-3 text-body font-semibold">
        <Percent className="h-4 w-4 text-primary" strokeWidth={1.5} />
        {discountCode ? `Edit ${discountCode.code}` : 'Create Discount Code'}
      </h2>
      <form
        className="max-w-2xl space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          saveCode.mutate()
        }}
      >
        <DiscountBasics
          form={form}
          setForm={setForm}
          error={saveCode.error}
          codeLocked={Boolean(discountCode)}
        />
        <DiscountRestrictions
          form={form}
          setForm={setForm}
          error={saveCode.error}
          levels={levelsQuery.data?.membership_levels ?? []}
          levelsLoading={levelsQuery.isLoading}
          levelsError={levelsQuery.isError}
          events={eventsQuery.data?.events ?? []}
          eventsLoading={eventsQuery.isLoading}
          eventsError={eventsQuery.isError}
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={saveCode.isPending || discountCode?.archived_at != null}>
            {saveCode.isPending
              ? 'Saving...'
              : discountCode
                ? 'Save changes'
                : 'Create Discount Code'}
          </Button>
        </div>
      </form>
    </section>
  )
}

function DiscountBasics({ form, setForm, error, codeLocked }: FormSectionProps) {
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_10rem]">
      <FormField label="Code" htmlFor="discount-code" error={apiFieldMessages(error, 'code')[0]}>
        <Input
          id="discount-code"
          disabled={codeLocked}
          value={form.code}
          onChange={(event) => setForm({ ...form, code: event.target.value })}
        />
      </FormField>
      <FormField
        label="Reduction type"
        htmlFor="discount-reduction-type"
        error={apiFieldMessages(error, 'reduction')[0]}
      >
        <Select
          value={form.reductionType}
          onValueChange={(reductionType) => setForm({ ...form, reductionType, amount: '' })}
        >
          <SelectTrigger id="discount-reduction-type" aria-label="Reduction type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="percent">Percent</SelectItem>
            <SelectItem value="fixed">Fixed amount</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
      <FormField
        label={form.reductionType === 'percent' ? 'Percent reduction' : 'Fixed reduction'}
        htmlFor="discount-reduction"
        error={
          apiFieldMessages(error, form.reductionType === 'percent' ? 'percent' : 'fixed_amount')[0]
        }
      >
        <Input
          id="discount-reduction"
          inputMode="decimal"
          value={form.amount}
          onChange={(event) => setForm({ ...form, amount: event.target.value })}
        />
      </FormField>
    </div>
  )
}

function DiscountRestrictions({
  form,
  setForm,
  error,
  levels,
  levelsLoading,
  levelsError,
  events,
  eventsLoading,
  eventsError,
}: RestrictionProps) {
  return (
    <fieldset className="space-y-3 border-t border-border pt-3">
      <legend className="text-body-sm font-medium text-foreground">Optional restrictions</legend>
      <div className="grid gap-3 sm:grid-cols-3">
        <RestrictionInput
          label="Usage cap"
          id="discount-usage-cap"
          value={form.usageCap}
          error={apiFieldMessages(error, 'usage_cap')[0]}
          inputMode="numeric"
          onChange={(usageCap) => setForm({ ...form, usageCap })}
        />
        <RestrictionInput
          label="Starts on"
          id="discount-starts-on"
          value={form.startsOn}
          error={apiFieldMessages(error, 'starts_on')[0]}
          type="date"
          onChange={(startsOn) => setForm({ ...form, startsOn })}
        />
        <RestrictionInput
          label="Ends on"
          id="discount-ends-on"
          value={form.endsOn}
          error={apiFieldMessages(error, 'ends_on')[0]}
          type="date"
          onChange={(endsOn) => setForm({ ...form, endsOn })}
        />
      </div>
      <DiscountOrigins form={form} setForm={setForm} error={error} />
      <MembershipLevelRestrictions
        form={form}
        setForm={setForm}
        levels={levels}
        loading={levelsLoading}
        failed={levelsError}
      />
      <DiscountCodeEventRestrictions
        eventIds={form.eventIds}
        events={events}
        loading={eventsLoading}
        failed={eventsError}
        onChange={(eventIds) => setForm({ ...form, eventIds })}
      />
    </fieldset>
  )
}

const DISCOUNT_ORIGINS = [
  { value: 'application', label: 'New joins' },
  { value: 'renewal', label: 'Renewals' },
  { value: 'level_change', label: 'Level changes' },
  { value: 'event', label: 'Event registrations' },
]

function DiscountOrigins({ form, setForm, error }: FormSectionProps) {
  return (
    <div>
      <p className="pb-2 text-caption font-medium text-foreground">Applies to</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {DISCOUNT_ORIGINS.map((origin) => (
          <label
            key={origin.value}
            className="flex items-center gap-2 text-body-sm text-foreground"
          >
            <Checkbox
              aria-label={origin.label}
              checked={form.appliesTo.includes(origin.value)}
              onCheckedChange={(checked) =>
                setForm({
                  ...form,
                  appliesTo: checked
                    ? [...form.appliesTo, origin.value]
                    : form.appliesTo.filter((value) => value !== origin.value),
                })
              }
            />
            {origin.label}
          </label>
        ))}
      </div>
      {apiFieldMessages(error, 'applies_to')[0] && (
        <p className="pt-1 text-caption text-destructive">
          {apiFieldMessages(error, 'applies_to')[0]}
        </p>
      )}
    </div>
  )
}

function RestrictionInput({
  label,
  id,
  value,
  error,
  inputMode,
  type,
  onChange,
}: RestrictionInputProps) {
  return (
    <FormField label={label} htmlFor={id} error={error}>
      <Input
        id={id}
        inputMode={inputMode}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormField>
  )
}

function MembershipLevelRestrictions({ form, setForm, levels, loading, failed }: LevelProps) {
  return (
    <div>
      <p className="pb-2 text-caption font-medium text-foreground">Membership Levels</p>
      {loading ? (
        <p className="text-caption text-muted-foreground">Loading Membership Levels...</p>
      ) : failed ? (
        <Alert>Membership Levels unavailable</Alert>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {levels.map((level) => (
            <label key={level.id} className="flex items-center gap-2 text-body-sm text-foreground">
              <Checkbox
                aria-label={level.name}
                checked={form.membershipLevelIds.includes(level.id)}
                onCheckedChange={(checked) =>
                  setForm({
                    ...form,
                    membershipLevelIds: checked
                      ? [...form.membershipLevelIds, level.id]
                      : form.membershipLevelIds.filter((id) => id !== level.id),
                  })
                }
              />
              {level.name}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

function discountCodePayload(form: DiscountCodeFormState, editing: boolean) {
  return {
    discount_code: {
      ...(editing ? {} : { code: form.code }),
      ...reductionPayload(form, editing),
      ...optionalRestriction('usage_cap', form.usageCap, editing),
      ...optionalRestriction('starts_on', form.startsOn, editing),
      ...optionalRestriction('ends_on', form.endsOn, editing),
      applies_to: form.appliesTo,
      ...membershipLevelPayload(form.membershipLevelIds, editing),
      ...eventPayload(form.eventIds, editing),
    },
  }
}

function reductionPayload(form: DiscountCodeFormState, editing: boolean) {
  if (form.reductionType === 'percent') {
    return { percent: form.amount, ...(editing ? { fixed_amount: null } : {}) }
  }

  return { fixed_amount: form.amount, ...(editing ? { percent: null } : {}) }
}

function optionalRestriction(field: string, value: string, editing: boolean) {
  if (editing) return { [field]: value || null }
  return value ? { [field]: value } : {}
}

function membershipLevelPayload(membershipLevelIds: number[], editing: boolean) {
  if (!editing && membershipLevelIds.length === 0) return {}
  return { membership_level_ids: membershipLevelIds }
}

function eventPayload(eventIds: number[], editing: boolean) {
  if (!editing && eventIds.length === 0) return {}
  return { event_ids: eventIds }
}

function formForDiscountCode(discountCode?: DiscountCode): DiscountCodeFormState {
  if (!discountCode) return EMPTY_FORM

  return {
    code: discountCode.code,
    reductionType: discountCode.percent == null ? 'fixed' : 'percent',
    amount:
      discountCode.percent == null
        ? (discountCode.fixed_amount ?? '').replace(/[^0-9.-]/g, '')
        : discountCode.percent.toString(),
    usageCap: discountCode.usage_cap?.toString() ?? '',
    startsOn: discountCode.starts_on ?? '',
    endsOn: discountCode.ends_on ?? '',
    appliesTo: discountCode.applies_to,
    membershipLevelIds: discountCode.membership_levels.map((level) => level.id),
    eventIds: (discountCode.events ?? []).map((event) => event.id),
  }
}

interface FormSectionProps {
  form: DiscountCodeFormState
  setForm: SetForm
  error: Error | null
  codeLocked?: boolean
}

interface RestrictionProps extends FormSectionProps {
  levels: MembershipLevelSummary[]
  levelsLoading: boolean
  levelsError: boolean
  events: EventSummary[]
  eventsLoading: boolean
  eventsError: boolean
}

interface RestrictionInputProps {
  label: string
  id: string
  value: string
  error?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  type?: React.HTMLInputTypeAttribute
  onChange: (value: string) => void
}

interface LevelProps {
  form: DiscountCodeFormState
  setForm: SetForm
  levels: MembershipLevelSummary[]
  loading: boolean
  failed: boolean
}
